<script>
  import { createEventDispatcher } from "svelte";
  import PlayerManagement from "../PlayerManagement.svelte";

  export let campaign = {};
  export let api = {};

  const dispatch = createEventDispatcher();

  let showAssignModal = false;
  let showPlayerManagement = false;
  let selectedPlayerIds = [];
  let selectedPlayerCustomerCodes = {};
  let selectedPlayerLoginNames = {};
  let selectedUsernameOrigins = {};
  let amount = "";
  let isAssigning = false;
  let isCancelling = false;
  let message = "";
  let error = "";
  let failedDetails = [];
  let bonusResults = [];

  $: isDeposit = campaign.providerCampaignType === "DEPOSIT";
  $: campaignStatusDisabled = Number(campaign.campaignStatus) !== 1;
  $: isFreebet = campaign.providerCampaignType === "FREEBET";
  $: freebetAmount = campaign.slotConfigs?.[0]?.amountPerRound || null;
  $: players = (campaign.playerList || [])
    .map((p) => {
      const activePromos = (p.deliveredPromos || []).filter((promo) => promo.status === 1);
      const firstPromo = activePromos[0] || {};
      return {
        ...p,
        activePromos,
        hasActive: activePromos.length > 0,
        assignmentType: activePromos[0]?.assignmentType || null,
        promoDepositAmount: firstPromo.depositAmount,
        promoBonusAmount: firstPromo.bonusAmount,
        promoRolloverAmount: firstPromo.rolloverAmount,
      };
    })
    .sort((a, b) => (a.hasActive ? -1 : 1));

  async function handleAssign() {
    if (selectedPlayerIds.length === 0) {
      alert("Selecciona al menos un jugador");
      return;
    }
    if (isDeposit && !amount) {
      alert("Debes ingresar el monto base del bono para campañas de tipo depósito");
      return;
    }

    isAssigning = true;
    message = "";
    error = "";
    failedDetails = [];
    bonusResults = [];
    try {
      const payload = {
        playerIds: selectedPlayerIds,
        playerCustomerCodes: selectedPlayerCustomerCodes,
        playerLoginNames: selectedPlayerLoginNames,
        usernameOrigins: selectedUsernameOrigins,
        amount: isDeposit ? Number(amount) : undefined,
      };
      const res = await api.assignPlayers(campaign.campaignId, payload);
      const data = res?.data || {};
      const successful = data.successful || 0;
      const failed = data.failed || 0;

      const details = data.details || [];
      const assigned = details.filter((d) => d.success && d.bonusCode);

      const bonusDetailsList = [];
      for (const player of assigned) {
        const bonusId = player.bonusCode?.replace("FIRST2_", "");
        if (bonusId) {
          try {
            const bonusRes = await api.getBonusDetails({
              bonusId,
              customerCode: player.customerCode,
              clientCode: campaign.client?.clientCode || "",
              campaignType: campaign.providerCampaignType || "",
            });
            const bd = bonusRes?.data || {};
            bonusDetailsList.push({
              playerId: player.playerId,
              bonusCode: player.bonusCode,
              depositAmount: bd.depositAmount,
              bonusAmount: bd.bonusAmount,
              rolloverAmount: bd.rolloverAmount,
            });
          } catch {
            bonusDetailsList.push({
              playerId: player.playerId,
              bonusCode: player.bonusCode,
              depositAmount: null,
              bonusAmount: null,
              rolloverAmount: null,
            });
          }
        }
      }

      bonusResults = bonusDetailsList;
      const summaryParts = [`Asignados: ${successful}`];
      if (failed > 0) summaryParts.push(`Fallidos: ${failed}`);
      for (const br of bonusDetailsList) {
        const parts = [br.playerId];
        if (br.depositAmount != null) parts.push(`dep ${br.depositAmount}`);
        if (br.bonusAmount != null) parts.push(`bonus ${br.bonusAmount}`);
        if (br.rolloverAmount != null) parts.push(`roll ${br.rolloverAmount}`);
        summaryParts.push(parts.join(" "));
      }
      message = summaryParts.join(" | ");

      failedDetails = details.filter((d) => !d.success);
      if (failedDetails.length > 0) {
        error = `Fallaron ${failedDetails.length} jugador(es)`;
      }

      selectedPlayerIds = [];
      amount = "";
      showAssignModal = false;
      dispatch("playersUpdated", { campaignId: campaign.campaignId });
    } catch (err) {
      console.error("Error assigning players:", err);
      error = "Error al asignar jugadores";
    } finally {
      isAssigning = false;
    }
  }

  let showPlayerDetails = false;
  let selectedPlayerDetails = null;
  let isFetchingDetails = false;

  async function handleViewDetails(p) {
    if (isFetchingDetails) return;
    isFetchingDetails = true;
    try {
      const promo = p.activePromos?.[0];
      const bonusCode = promo?.bonusCode || "";
      const bonusId = bonusCode.replace(/^(FIRST2_|DEPOSIT_|FREEBET_)/, "");
      const clientCode = campaign.client?.clientCode || "";
      const res = isDeposit
        ? await api.getBonusDebit(bonusId, p.playerId, clientCode)
        : await api.getBonusFree(bonusId, p.playerId, clientCode);
      const raw = res?.data ?? res;
      let detail = raw;
      if (raw?.data && Array.isArray(raw.data)) detail = raw.data[0];
      else if (Array.isArray(raw)) detail = raw[0];
      selectedPlayerDetails = detail;
      showPlayerDetails = true;
    } catch (err) {
      console.error(err);
      alert("No se pudo obtener los detalles del jugador.");
    } finally {
      isFetchingDetails = false;
    }
  }

  function fmtDate(d) {
    if (!d) return "—";
    try { return new Date(d).toLocaleString(); } catch { return "—"; }
  }

  function expType(c) {
    return c.uExpirationTimeType || c.uexpirationTimeType || null;
  }
  function expDays(c) {
    return c.uDaysTillExpiration ?? c.udaysTillExpiration ?? null;
  }
  function getExpiry(c) {
    return c.firstExpiredAt || null;
  }

  const DEPOSIT_BONUS_STATUS = {
    1: { label: "Bloqueado", class: "bg-secondary text-light" },
    2: { label: "Activo", class: "bg-success" },
    3: { label: "Cancelado", class: "bg-danger" },
    4: { label: "Expirado", class: "bg-warning text-dark" },
    5: { label: "Liberándose", class: "bg-info text-dark" },
    6: { label: "Liberado", class: "bg-primary" },
    7: { label: "Rollover", class: "bg-info text-dark" },
    8: { label: "Agotado", class: "bg-dark" },
  };

  const FREEBET_BONUS_STATUS = {
    0: { label: "Inactivo", class: "bg-secondary text-light" },
    1: { label: "Activo", class: "bg-success" },
    2: { label: "Usado", class: "bg-primary" },
    3: { label: "Expirado", class: "bg-warning text-dark" },
    4: { label: "Expirado", class: "bg-warning text-dark" },
    5: { label: "Fallido", class: "bg-danger" },
    6: { label: "Cancelado", class: "bg-danger" },
    7: { label: "Listo", class: "bg-info text-dark" },
  };

  function getFirstStatusInfo(status, mapping) {
    return mapping[status] || { label: "Desconocido (" + status + ")", class: "bg-light text-dark" };
  }

  $: statusInfo = selectedPlayerDetails
    ? getFirstStatusInfo(selectedPlayerDetails.status, isDeposit ? DEPOSIT_BONUS_STATUS : FREEBET_BONUS_STATUS)
    : null;

  async function handleCancel(player) {
    if (!confirm(`¿Cancelar bono para el jugador ${player.playerId}?`)) return;
    isCancelling = true;
    try {
      await api.cancelPlayers(campaign.campaignId, {
        playerIds: [player.playerId],
      });
      dispatch("playersUpdated", { campaignId: campaign.campaignId });
    } catch (err) {
      console.error("Error cancelling player:", err);
      alert("Error al cancelar jugador");
    } finally {
      isCancelling = false;
    }
  }
</script>

<div class="modal-backdrop fade show"></div>
<div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.3);">
  <div class="modal-dialog modal-lg modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header py-2">
        <h6 class="modal-title mb-0">
          {campaign.campaignName || campaign.campaignId}
          {#if isFreebet && freebetAmount}
            <span class="badge bg-success ms-2">{freebetAmount} {campaign.currency || ''}</span>
          {/if}
        </h6>
        <button type="button" class="btn-close" on:click={() => dispatch("close")}></button>
      </div>

      <div class="modal-body py-2">
        {#if message}
          <div class="alert alert-success py-1 px-2 mb-2 small">{message}</div>
        {/if}
        {#if error}
          <div class="alert alert-danger py-1 px-2 mb-2 small">
            {error}
            {#if failedDetails.length > 0}
              <ul class="mb-0 mt-1 ps-3">
                {#each failedDetails as fd}
                  <li>
                    Jugador {fd.playerId}: {fd.message || (fd.errorCode ? "Error del proveedor" : "Error desconocido")}
                  </li>
                {/each}
              </ul>
            {/if}
          </div>
        {/if}

        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="fw-semibold small mb-0">
            Asignados: {players.length}
          </span>
          {#if isDeposit}
              <button
                class="btn btn-primary btn-sm"
                on:click={() => (showAssignModal = true)}
                disabled={campaignStatusDisabled}
                title={campaignStatusDisabled ? "Campaña desactivada en FIRST" : ""}
              >
                Simular depósito
              </button>
            {:else}
              <button
                class="btn btn-primary btn-sm"
                on:click={() => (showPlayerManagement = true)}
                disabled={campaignStatusDisabled}
                title={campaignStatusDisabled ? "Campaña desactivada en FIRST" : ""}
              >
                Asignación manual
              </button>
            {/if}
        </div>

        <div class="text-muted small mb-2">
          <span>Creada: {fmtDate(campaign.firstCreatedAt)}</span>
          {#if getExpiry(campaign)}
            <span> · Expira: {fmtDate(getExpiry(campaign))}</span>
          {:else if expType(campaign) === "DTE" && expDays(campaign)}
            <span> · Vence: {expDays(campaign)} días</span>
          {/if}
        </div>

        <div class="players-table-wrap">
          <table class="table table-sm table-striped table-hover table-bordered mb-0">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Bonus Code</th>
                {#if isDeposit}
                  <th>Depósito</th>
                  <th>Bono</th>
                  <th>Rollover</th>
                {:else}
                  <th>Monto</th>
                {/if}
                <th>Tipo</th>
                <th>Estado</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#if players.length === 0}
                <tr>
                  <td colspan={isDeposit ? 9 : 7} class="text-center text-muted small">Sin jugadores</td>
                </tr>
              {:else}
                {#each players as p}
                    {@const bonusInfo = bonusResults.find((b) => b.bonusCode === p.activePromos?.[0]?.bonusCode)}
                  <tr>
                    <td class="small">{p.playerId}</td>
                    <td class="small">{p.playerName || '—'}</td>
                    <td class="small">
                      {#each p.activePromos as promo}
                        <span>{promo.bonusCode}</span>
                      {:else}
                        <span class="text-muted">—</span>
                      {/each}
                    </td>
                    {#if isDeposit}
                      <td class="small">
                        {#if p.promoDepositAmount != null}
                          <span>{p.promoDepositAmount}</span>
                        {:else if bonusInfo?.depositAmount}
                          <span>{bonusInfo.depositAmount}</span>
                        {:else}
                          <span class="text-muted">—</span>
                        {/if}
                      </td>
                      <td class="small">
                        {#if p.promoBonusAmount != null}
                          <span class="text-success fw-bold">{p.promoBonusAmount}</span>
                        {:else if bonusInfo?.bonusAmount != null}
                          <span class="text-success fw-bold">{bonusInfo.bonusAmount}</span>
                        {:else}
                          <span class="text-muted">—</span>
                        {/if}
                      </td>
                      <td class="small">
                        {#if p.promoRolloverAmount != null}
                          <span>{p.promoRolloverAmount}</span>
                        {:else if bonusInfo?.rolloverAmount}
                          <span>{bonusInfo.rolloverAmount}</span>
                        {:else}
                          <span class="text-muted">—</span>
                        {/if}
                      </td>
                    {:else}
                      <td class="small">
                        {#if p.promoBonusAmount != null}
                          <span class="text-success fw-bold">{p.promoBonusAmount}</span>
                        {:else}
                          <span class="text-muted">—</span>
                        {/if}
                      </td>
                    {/if}
                    <td class="small">
                      {#if p.assignmentType === "MANUAL"}
                        <span class="badge bg-info">Manual</span>
                      {:else if p.assignmentType === "AUTOMATIC"}
                        <span class="badge bg-warning text-dark">Auto</span>
                      {:else}
                        <span class="text-muted">—</span>
                      {/if}
                    </td>
                    <td class="small">
                      {#if p.hasActive}
                        <span class="badge bg-success">Activo</span>
                      {:else}
                        <span class="badge bg-secondary">Inactivo</span>
                      {/if}
                    </td>
                    <td class="text-end">
                      {#if p.hasActive}
                        <button
                          class="btn btn-outline-primary btn-sm py-0 px-1 me-1"
                          on:click={() => handleViewDetails(p)}
                          disabled={isFetchingDetails}
                          title="Seguimiento"
                        >
                          <i class="fas fa-eye"></i>
                        </button>
                        <button
                          class="btn btn-outline-danger btn-sm py-0 px-1"
                          on:click={() => handleCancel(p)}
                          disabled={isCancelling}
                          title="Cancelar"
                        >
                          &times;
                        </button>
                      {/if}
                    </td>
                  </tr>
                {/each}
              {/if}
            </tbody>
          </table>
        </div>
      </div>

      <div class="modal-footer py-2">
        <button class="btn btn-secondary btn-sm" on:click={() => dispatch("close")}>
          Cerrar
        </button>
      </div>
    </div>
  </div>
</div>

{#if showAssignModal}
  <div class="modal-backdrop fade show" style="z-index:1050;"></div>
  <div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.5);z-index:1055;">
    <div class="modal-dialog modal-dialog-centered modal-assign">
      <div class="modal-content">
        <div class="modal-header py-2">
          <h6 class="modal-title mb-0">{isFreebet ? "Asignación manual" : "Simular depósito"}</h6>
          <button type="button" class="btn-close btn-close-sm" on:click={() => { showAssignModal = false; selectedPlayerIds = []; amount = ""; }}></button>
        </div>
        <div class="modal-body py-2">
          {#if isFreebet && freebetAmount}
            <div class="mb-2 small">
              Monto fijo del freebet: <strong>{freebetAmount} {campaign.currency || ''}</strong>
            </div>
          {/if}
          {#if isDeposit}
            <div class="mb-2">
              <label for="depositAmountV2" class="form-label small fw-semibold mb-1">
                Monto base <span class="text-danger">*</span>
              </label>
              <input
                id="depositAmountV2"
                type="number"
                class="form-control form-control-sm"
                bind:value={amount}
                min="0"
                step="0.01"
                placeholder="Ej: 100"
              />
              <small class="text-muted">First calculará el bono según la campaña</small>
            </div>
          {/if}

          {#if selectedPlayerIds.length > 0}
            <div class="mb-1">
              <small class="text-muted">{selectedPlayerIds.length} jugador(es)</small>
            </div>
          {/if}
          <button
            class="btn btn-outline-primary btn-sm w-100"
            on:click={() => (showPlayerManagement = true)}
          >
            Seleccionar jugadores...
          </button>
        </div>
        <div class="modal-footer py-2">
          <button class="btn btn-secondary btn-sm" on:click={() => { showAssignModal = false; selectedPlayerIds = []; amount = ""; }}>
            Cancelar
          </button>
          <button
            class="btn btn-primary btn-sm"
            on:click={handleAssign}
            disabled={isAssigning || selectedPlayerIds.length === 0}
          >
            {isAssigning ? "Asignando..." : "Asignar"}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

{#if showPlayerDetails && selectedPlayerDetails}
  <div class="modal-backdrop fade show" style="z-index:1055;"></div>
  <div class="modal d-block" tabindex="-1" style="background:rgba(0,0,0,.4);z-index:1060;">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content shadow-lg border-0 rounded-4">
        <div class="modal-header border-bottom-0 pb-0">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <h5 class="modal-title fw-bold fs-4 mb-0">Detalles del Bono - FIRST V2</h5>
            <span class="badge bg-light text-dark border fw-normal">
              ID: #{selectedPlayerDetails?.id || "—"}
            </span>
            <span class="badge px-2 py-1 {statusInfo?.class}">{statusInfo?.label}</span>
          </div>
          <button type="button" class="btn-close" on:click={() => (showPlayerDetails = false)}></button>
        </div>
        <div class="modal-body px-4 pt-3 pb-4 overflow-auto" style="max-height: 75vh;">
          <div class="row g-2 mb-3">
            {#if isDeposit}
              <div class="col-md-4">
                <div class="border rounded-3 py-3 px-2 text-center h-100">
                  <div class="text-muted small text-uppercase fw-bold">Depositado</div>
                  <div class="fs-5 fw-bold">{selectedPlayerDetails.depositAmount ?? 0}</div>
                  <div class="text-muted small">{campaign.currency || "USD"}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="border rounded-3 py-3 px-2 text-center h-100">
                  <div class="text-muted small text-uppercase fw-bold">Bono</div>
                  <div class="fs-5 fw-bold text-success">{selectedPlayerDetails.bonusAmount ?? 0}</div>
                  <div class="text-muted small">{campaign.currency || "USD"}</div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="border rounded-3 py-3 px-2 text-center h-100">
                  <div class="text-muted small text-uppercase fw-bold">Saldo del Bono</div>
                  <div class="fs-5 fw-bold">{selectedPlayerDetails.bonusBalance ?? 0}</div>
                  <div class="text-muted small">{campaign.currency || "USD"}</div>
                </div>
              </div>
            {:else}
              <div class="col-md-6">
                <div class="border rounded-3 py-3 px-2 text-center h-100">
                  <div class="text-muted small text-uppercase fw-bold">Monto</div>
                  <div class="fs-5 fw-bold text-success">{selectedPlayerDetails.amount ?? 0}</div>
                  <div class="text-muted small">{campaign.currency || "USD"}</div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="border rounded-3 py-3 px-2 text-center h-100">
                  <div class="text-muted small text-uppercase fw-bold">Gastado</div>
                  <div class="fs-5 fw-bold">{selectedPlayerDetails.spend ?? 0}</div>
                  <div class="text-muted small">{campaign.currency || "USD"}</div>
                </div>
              </div>
            {/if}
          </div>

          {#if isDeposit}
            <div class="border rounded-3 p-3 mb-3">
              <div class="d-flex justify-content-between align-items-end mb-1">
                <span class="text-muted small text-uppercase fw-bold">Progreso de Rollover</span>
                <span class="badge bg-primary">
                  {selectedPlayerDetails.rolloverAmount
                    ? ((selectedPlayerDetails.spend || 0) / selectedPlayerDetails.rolloverAmount * 100).toFixed(2)
                    : 0}%
                </span>
              </div>
              <div class="d-flex align-items-center gap-2 mb-1">
                <span class="fs-6 fw-bold">{selectedPlayerDetails.spend ?? 0}</span>
                <span class="text-muted small">/ {selectedPlayerDetails.rolloverAmount ?? 0} {campaign.currency || "USD"}</span>
              </div>
              <div class="progress" style="height: 8px;">
                <div class="progress-bar bg-primary" role="progressbar"
                  style="width: {selectedPlayerDetails.rolloverAmount
                    ? Math.min(100, (selectedPlayerDetails.spend || 0) / selectedPlayerDetails.rolloverAmount * 100)
                    : 0}%">
                </div>
              </div>
            </div>
          {/if}

          <div class="row g-3">
            <div class="col-sm-6">
              <div class="text-muted small text-uppercase fw-bold mb-1">Asignado el</div>
              <div class="fw-bold">{fmtDate(selectedPlayerDetails.createdAt)}</div>
            </div>
            <div class="col-sm-6">
              <div class="text-muted small text-uppercase fw-bold mb-1">Vence el</div>
              <div class="fw-bold">{fmtDate(isDeposit ? selectedPlayerDetails.expiredAt : selectedPlayerDetails.activeTill)}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer py-2">
          <button class="btn btn-secondary btn-sm" on:click={() => (showPlayerDetails = false)}>Cerrar</button>
        </div>
      </div>
    </div>
  </div>
{/if}

{#if showPlayerManagement}
  <PlayerManagement
    clientId={campaign._resolvedClientId}
    currency={campaign.currency}
    initialSelected={selectedPlayerIds}
    on:close={() => (showPlayerManagement = false)}
    on:confirm={(e) => {
      selectedPlayerIds = e.detail.ids;
      const playerList = e.detail.players || [];
      selectedPlayerCustomerCodes = {};
      selectedPlayerLoginNames = {};
      const usernameOriginsMap = {};
      for (const p of playerList) {
        if (p.id) {
          if (p.username) selectedPlayerCustomerCodes[p.id] = p.username;
          if (p.label) selectedPlayerLoginNames[p.id] = p.label;
          if (p.username_origin) usernameOriginsMap[p.id] = p.username_origin;
        }
      }
      selectedUsernameOrigins = usernameOriginsMap;
      showPlayerManagement = false;

      if (isFreebet && selectedPlayerIds.length > 0) {
        handleAssign();
      }
    }}
  />
{/if}

<style>
  .players-table-wrap {
    height: 500px;
    overflow-y: auto;
  }
  .players-table-wrap :global(thead th) {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 1;
  }
  .modal-assign {
    max-width: 400px;
  }
</style>
